import { Container, Graphics, Text } from "pixi.js"
import { TEXT_ROLES } from "../text/typography"
import { TextRenderer } from "../text/text_renderer"
import {
    DIAL_CHIP_TEXT_COLOR,
    DialMetrics,
    dialArcRadius,
    dialBottomExtent,
    dialFaceRadius,
    dialSize,
    drawDialChip,
    drawDialFace,
    drawDialGauge,
    easeOutCubic,
    interpolateColor,
} from "./dial"

// The HUD dial that counts DOWN to something the player has to be ready for — in an ember camp, the
// next assault landing on the fire.
//
// It shares its chrome and its screen slot with the WAVE BADGE (wave_indicator.ts): this dial drains
// to zero and vanishes, and the badge grows into the same spot already full, so the HUD reads as one
// object changing state rather than two widgets taking turns. The shared parts live in dial.ts; what
// is this widget's own is the PALETTE (green while there is time, amber, then red) and the SECOND —
// every whole second the numeral punches in and a ring ripples off the dial, which is what makes a
// countdown feel like it is running rather than merely being displayed.
//
// Everything animates off the frame delta handed to `update`, so the widget never owns a timer. The
// caller only has to stop calling it when it is no longer `visible`.

// Entrance and exit, in ms. The exit is quicker: by then the thing it was counting to has arrived
// and is what the player should be looking at.
const APPEAR_MS = 190
const VANISH_MS = 240

// How long a fresh numeral takes to settle back from its punch-in, and how much bigger it starts.
const TICK_MS = 260
const TICK_SWELL = 0.3

// The ring that ripples off the dial on each whole second, and how far past the dial's edge it gets.
// It starts OUTSIDE the rim and leaves immediately: a ring sitting ON the dial reads as the gauge,
// which at one second left said "full" at the exact moment the gauge said "empty".
const RIPPLE_MS = 420
const RIPPLE_REACH = 15

// Inside this many seconds the dial is urgent: the glow breathes, the face takes a red wash and the
// ripples read as an alarm rather than a tick.
const URGENT_SECONDS = 5

// Gauge ramp: plenty of time is green, the middle is amber, out of time is red.
const SAFE_COLOR = 0x4ade80
const WARN_COLOR = 0xfbbf24
const DANGER_COLOR = 0xef4444

const CAPTION_COLOR = 0xffc89a
const CAPTION_SIZE = TEXT_ROLES.compactCaption.fontSize
const CHIP_TEXT_SIZE = TEXT_ROLES.compactBody.fontSize

export interface CountdownTimerProps {
    radius?: number
    strokeWidth?: number
    backgroundColor?: number
    textColor?: number
    onComplete?: () => void
}

/** Green (plenty of time) -> amber (half) -> red (out of time). */
export const countdownColor = (progress: number): number => {
    if (progress > 0.5) {
        return interpolateColor(WARN_COLOR, SAFE_COLOR, (progress - 0.5) / 0.5)
    }
    return interpolateColor(DANGER_COLOR, WARN_COLOR, Math.max(0, progress) / 0.5)
}

export class CountdownTimer extends Container {
    private metrics: DialMetrics
    private backgroundColor?: number
    private textColor: number
    private onComplete?: () => void

    private face: Graphics
    private fx: Graphics
    private gauge: Graphics
    private chip: Graphics
    private textDisplay: Text
    private captionText: Text
    private chipText: Text

    private currentTime: number = 0
    private totalTime: number = 0
    private isRunning: boolean = false

    // What the numeral currently reads, so a second that hasn't flipped costs no re-rasterization.
    private lastDisplayTime: number = -1
    // The wave this is counting down to, when the camp told us. Zero means no chip.
    private wave: number = 0

    // Whether the dial wants to be on screen, and how far the entrance/exit has got (0..1).
    private wanted: boolean = false
    private appear: number = 0
    // Age of the current numeral, in ms. Drives its punch-in and the ripple behind it.
    private tickAge: number = TICK_MS
    // Free-running clock for the urgent breathing, so it doesn't stall when the arc does.
    private pulseTs: number = 0

    constructor(props: CountdownTimerProps = {}) {
        super()

        this.metrics = { radius: props.radius || 50, strokeWidth: props.strokeWidth || 8 }
        // Kept for callers that still pass one, but the ember dials share a face now: an override
        // would be the one thing that breaks the hand-over to the wave badge.
        this.backgroundColor = props.backgroundColor
        this.textColor = props.textColor || 0xffffff
        this.onComplete = props.onComplete

        this.face = new Graphics()
        this.fx = new Graphics()
        this.gauge = new Graphics()
        this.chip = new Graphics()

        this.textDisplay = new TextRenderer().color(this.textColor).role("heading").bold().align("center").render("0") as Text
        this.textDisplay.anchor.set(0.5)
        this.captionText = new TextRenderer()
            .color(CAPTION_COLOR)
            .fontSize(CAPTION_SIZE)
            .bold()
            .align("center")
            .render("INCOMING") as Text
        this.captionText.anchor.set(0.5)
        this.captionText.alpha = 0.75
        this.chipText = new TextRenderer()
            .color(DIAL_CHIP_TEXT_COLOR)
            .fontSize(CHIP_TEXT_SIZE)
            .bold()
            .align("center")
            .render("WAVE 1") as Text
        this.chipText.anchor.set(0.5)

        // The ripple goes BEHIND the gauge so an alarm ring never washes out the arc it came from.
        this.addChild(this.face, this.fx, this.gauge, this.textDisplay, this.captionText, this.chip, this.chipText)

        this.drawFace()
        this.layoutText()

        this.visible = false
        this.alpha = 0
    }

    // ---------------------------------------------------------------- geometry

    /** Outer painted size of the dial itself — what a caller should position against. */
    public get dialSize(): number {
        return dialSize(this.metrics)
    }

    /** How far below the dial's CENTRE the widget paints, chip included. */
    public get bottomExtent(): number {
        return dialBottomExtent(this.metrics, CHIP_TEXT_SIZE)
    }

    // ---------------------------------------------------------------- control

    /**
     * Start counting down.
     *
     * `wave` is the assault this is counting to, when the camp knows it: it captions the dial with
     * the same pill the wave badge will be wearing a moment later. Omit it for a bare timer.
     */
    public start(totalSeconds: number, wave: number = 0) {
        this.totalTime = totalSeconds
        this.currentTime = totalSeconds
        this.isRunning = true
        this.wave = wave > 0 ? Math.floor(wave) : 0
        this.lastDisplayTime = -1
        // The entrance carries the first numeral in; punching it as well reads as a stutter.
        this.tickAge = TICK_MS
        this.wanted = true
        this.appear = 0
        this.visible = true
        this.alpha = 0

        this.redraw()
    }

    public reset() {
        this.currentTime = this.totalTime
        this.isRunning = false
        this.lastDisplayTime = -1
        this.tickAge = TICK_MS
        this.wanted = true
        this.appear = 1
        this.alpha = 1
        this.visible = true
        this.scale.set(1)
        this.redraw()
    }

    /** Halt without finishing. The dial fades out; a caller that hides it outright also may. */
    public stop() {
        this.isRunning = false
        this.wanted = false
    }

    public getCurrentTime(): number {
        return this.currentTime
    }

    public getTotalTime(): number {
        return this.totalTime
    }

    public isActive(): boolean {
        return this.isRunning
    }

    /** Test/inspection seam: how far the entrance (or exit) has got, 0..1. */
    public appearance(): number {
        return this.appear
    }

    // ---------------------------------------------------------------- frame

    /**
     * Advance one frame. `deltaTime` is in milliseconds.
     *
     * Always does its animation work, even once the count has reached zero: the exit is part of the
     * countdown, and a widget that stopped moving the instant it hit zero would be yanked away at
     * the one moment the player is watching it.
     */
    public update(deltaTime: number) {
        if (this.isRunning) {
            this.currentTime -= deltaTime / 1000
            if (this.currentTime <= 0) {
                this.currentTime = 0
                this.isRunning = false
                this.onComplete?.()
                // Zero is reached, so the dial has said everything it has to say.
                this.wanted = false
            }
        }

        const target = this.wanted ? 1 : 0
        if (this.appear !== target) {
            const step = deltaTime / (this.wanted ? APPEAR_MS : VANISH_MS)
            this.appear = target > this.appear ? Math.min(1, this.appear + step) : Math.max(0, this.appear - step)
        }

        this.tickAge += deltaTime
        this.pulseTs += deltaTime

        if (!this.wanted && this.appear <= 0) {
            this.visible = false
            this.alpha = 0
            return
        }

        this.redraw()
    }

    // ---------------------------------------------------------------- drawing

    private redraw() {
        const eased = easeOutCubic(this.appear)
        this.alpha = eased
        // Grows into place and shrinks away rather than popping — the wave badge that replaces it
        // arrives the same way, from the same spot.
        this.scale.set(0.84 + 0.16 * eased)

        const displayTime = Math.ceil(this.currentTime)
        if (displayTime !== this.lastDisplayTime) {
            this.lastDisplayTime = displayTime
            this.textDisplay.text = String(Math.max(0, displayTime))
            this.tickAge = 0
        }

        const progress = this.totalTime > 0 ? Math.max(0, Math.min(1, this.currentTime / this.totalTime)) : 0
        const color = countdownColor(progress)
        const urgency = this.urgency()

        // A fresh numeral punches in and settles back. `tickAge` is wall-clock, not the fraction of
        // the remaining second, so the swell lasts the same 260ms whatever the frame rate — and it
        // happens when the number CHANGES, which is the thing the player is reacting to.
        const tick = 1 - easeOutCubic(Math.min(1, this.tickAge / TICK_MS))
        this.textDisplay.scale.set(1 + TICK_SWELL * tick)
        // Warm the numeral toward the ring colour as time drains: crisp white while there is plenty,
        // reddening for urgency near zero. A tint is a cheap multiply over white text, so it never
        // re-rasterizes the glyphs — but it can only ever DARKEN, so the mix is capped well short of
        // the ring's own colour. Taking it the whole way left the last few seconds, the ones that
        // actually have to be read, as dim red numerals on a dark red face.
        this.textDisplay.tint = interpolateColor(0xffffff, color, Math.min(0.5, Math.pow(1 - progress, 1.6)))
        this.captionText.alpha = 0.6 + 0.35 * urgency

        this.drawRipple(color, urgency)
        this.drawWash(urgency)
        this.drawGauge(progress, color, urgency)
        this.layoutChip()
    }

    private urgency(): number {
        if (!(this.totalTime > 0) || this.currentTime <= 0) {
            return 0
        }
        const window = Math.min(URGENT_SECONDS, this.totalTime)
        return Math.max(0, Math.min(1, (window - this.currentTime) / window))
    }

    private drawFace() {
        drawDialFace(this.face, { ...this.metrics, faceColor: this.backgroundColor, hourMarks: true })
    }

    // One expanding ring per second. Cheap (a single stroked circle) and it is what carries the
    // tick: the dial visibly emits something every time the number changes.
    private drawRipple(color: number, urgency: number) {
        this.fx.clear()
        const age = this.tickAge / RIPPLE_MS
        if (age >= 1 || this.currentTime <= 0) {
            return
        }
        const grow = easeOutCubic(age)
        const radius = dialFaceRadius(this.metrics) + 1 + RIPPLE_REACH * grow
        this.fx.lineStyle({ width: 2 + urgency, color, alpha: (0.26 + 0.2 * urgency) * Math.pow(1 - grow, 1.5) })
        this.fx.drawCircle(0, 0, radius)
    }

    // A red flush over the face in the final seconds, breathing with the glow. Drawn over the face
    // rather than into it so the face itself stays a one-off draw.
    private drawWash(urgency: number) {
        if (urgency <= 0) {
            return
        }
        const breath = 0.5 + 0.5 * Math.sin(this.pulseTs / 170)
        this.fx.beginFill(DANGER_COLOR, 0.07 * urgency + 0.07 * urgency * breath)
        this.fx.drawCircle(0, 0, dialFaceRadius(this.metrics) - 1)
        this.fx.endFill()
    }

    private drawGauge(progress: number, color: number, urgency: number) {
        if (this.currentTime <= 0) {
            this.gauge.clear()
            return
        }
        const breath = urgency > 0 ? 0.5 + 0.5 * Math.sin(this.pulseTs / 170) : 0
        drawDialGauge(this.gauge, {
            ...this.metrics,
            progress,
            color,
            haloWidth: 5 + 6 * urgency * breath,
            haloAlpha: 0.2 + 0.26 * urgency * breath,
        })
    }

    private layoutText() {
        // The numeral sits a little above centre to leave its caption room underneath — the same
        // split the wave badge uses for its own count and "LEFT".
        this.textDisplay.y = -7
        this.captionText.y = 15
        this.layoutChip()
    }

    private layoutChip() {
        const label = this.wave > 0 ? `WAVE ${this.wave}` : ""
        const show = label.length > 0
        this.chip.visible = show
        this.chipText.visible = show
        if (!show) {
            return
        }
        if (this.chipText.text !== label) {
            this.chipText.text = label
        }
        this.chipText.y = drawDialChip(this.chip, this.metrics, this.chipText.width, this.chipText.height)
    }
}

// The arc the gauge is stroked along. Exported for the layout tests, which assert the countdown dial
// and the wave badge share one geometry.
export const countdownArcRadius = (metrics: DialMetrics): number => dialArcRadius(metrics)
